import {
  AlarmClock,
  Award,
  Bell,
  CalendarClock,
  ClipboardCheck,
  FilePlus2,
  Gift,
  GraduationCap,
  Mail,
  Megaphone,
  MessageCircle,
  PlaySquare,
  ReceiptText,
  Reply,
  Tags,
  Trophy,
  UserPlus,
  Wrench,
  type LucideIcon,
} from "lucide-react";

type NotificationAppearance = {
  Icon: LucideIcon;
  iconClassName: string;
};

const DEFAULT_APPEARANCE: NotificationAppearance = {
  Icon: Bell,
  iconClassName:
    "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

const appearances: Record<string, NotificationAppearance> = {
  goldStorePresentDelivered: {
    Icon: Gift,
    iconClassName:
      "bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300",
  },
  goldStorePresentCancelled: {
    Icon: Gift,
    iconClassName:
      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
  contactMessage: {
    Icon: Mail,
    iconClassName:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  contactReplied: {
    Icon: Reply,
    iconClassName:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300",
  },
  invoiceApproved: {
    Icon: ReceiptText,
    iconClassName:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  invoiceCreated: {
    Icon: FilePlus2,
    iconClassName:
      "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300",
  },
  newComment: {
    Icon: MessageCircle,
    iconClassName:
      "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  },
  newLesson: {
    Icon: GraduationCap,
    iconClassName:
      "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  },
  newShort: {
    Icon: PlaySquare,
    iconClassName:
      "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  },
  newOffer: {
    Icon: Tags,
    iconClassName:
      "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950 dark:text-fuchsia-300",
  },
  newQuiz: {
    Icon: ClipboardCheck,
    iconClassName: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  },
  newAnnouncement: {
    Icon: Megaphone,
    iconClassName:
      "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  },
  quizResultReady: {
    Icon: ClipboardCheck,
    iconClassName:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  liveSessionReminder: {
    Icon: AlarmClock,
    iconClassName:
      "bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300",
  },
  subscriptionExpiryReminder: {
    Icon: CalendarClock,
    iconClassName:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  trainingGroundQuestionFeedbackResolved: {
    Icon: Wrench,
    iconClassName:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  gamificationLevelUp: {
    Icon: Trophy,
    iconClassName:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300",
  },
  gamificationLeagueResult: {
    Icon: Trophy,
    iconClassName:
      "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground",
  },
  leagueMonthResult: {
    Icon: Trophy,
    iconClassName:
      "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground",
  },
  leagueYearResult: {
    Icon: Trophy,
    iconClassName:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  leaguePassed: {
    Icon: Trophy,
    iconClassName:
      "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  },
  leagueLastDay: {
    Icon: AlarmClock,
    iconClassName:
      "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  },
  certificateIssued: {
    Icon: Award,
    iconClassName:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  newStudentSignup: {
    Icon: UserPlus,
    iconClassName:
      "bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300",
  },
  quizNeedsReview: {
    Icon: ClipboardCheck,
    iconClassName:
      "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  },
};

/**
 * Resolve the icon and color treatment for a notification type.
 *
 * @example
 * const appearance = getNotificationAppearance(notification.type);
 */
export function getNotificationAppearance(type: string): NotificationAppearance {
  return appearances[type] || DEFAULT_APPEARANCE;
}
